import React, { SyntheticEvent, useEffect, useState } from 'react'
import { ExpandMore } from '@mui/icons-material'
import { Box, Button, Collapse, Typography } from '@mui/material'
import { isFeatureFlagEnabled } from 'src/rsmCoreComponents/utils/featureFlagUtils'
import { useSelector } from 'react-redux'
import { getCemFeatures, getUserInfo } from 'src/store/userInfo/userInfoSlice'
import { useTranslation } from 'react-i18next'
import {
  AutopayDetailsResponseViewModel,
  ClientInfoViewModel,
  AutoPayRecurrenceInterval,
} from '@rsmus/ecp-financeservice'
import { useDeviceType } from 'src/rsmCoreComponents/hooks/useDeviceType'
import api from 'src/api'
import { AutopayIcon } from 'src/components/icons'
import { Styles } from '../../../types'
import { tokens } from '../../../styles/materialTheme'
import { getNextProcessingDate } from '../Invoices/dateUtils'
import { AutoPayFrequencyIntervals } from '../Invoices/PaymentComponents/AutoPayFrequency'
import { formatCurrency } from '../../../rsmCoreComponents/utils/formatters'

interface AutopayDetailsSectionProps {
  setOpenAutopayCancelDialog: React.Dispatch<React.SetStateAction<boolean>>
  handlePayOnAccountAutoPay: (
    e: SyntheticEvent,
    isPayOnAccount: boolean,
  ) => Promise<void>
  client: ClientInfoViewModel | null
}

interface FrequencyOption {
  value: AutoPayRecurrenceInterval
  label: string
}

const styles: Styles = {
  amount: (theme) => ({
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    marginBottom: '.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.375rem',
      lineHeight: '1.5rem',
    },
  }),
  autoPay: (theme) => ({
    fontFamily: 'Prelo-Book',
    color: theme.palette.grey[800],
    fontWeight: 500,
    fontSize: '1.065rem',
    fontStyle: 'italic',
    [theme.breakpoints.only('mobile')]: {
      fontWeight: 700,
    },
  }),
  updateButton: (theme) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    fontWeight: 600,
  }),
  cancelButton: (theme) => ({
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    fontWeight: 500,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: '0.625rem',
    marginRight: 'auto',
    [theme.breakpoints.only('mobile')]: {
      padding: 0,
      textDecoration: 'underline',
      placeContent: 'flex-start',
    },
  }),
  checkCircle: (theme) => ({
    color: theme.palette.secondary.main,
    [theme.breakpoints.only('desktop')]: {
      alignContent: 'center',
      marginBottom: 'auto',
    },
    [theme.breakpoints.only('mobile')]: {
      paddingRight: '0.25rem',
    },
    [theme.breakpoints.only('tablet')]: {
      paddingRight: '0.25rem',
    },
  }),
  autoPayIconContainer: (theme) => ({
    display: 'flex',
    height: 'fit-content',
    marginBottom: '0.5rem',
    font: 'Prelo',
    fontStyle: 'italic',
    [theme.breakpoints.only('desktop')]: {
      minWidth: '15.625rem',
    },
    [theme.breakpoints.only('mobile')]: {
      marginTop: '1rem',
      marginRight: '0.5rem',
    },
    [theme.breakpoints.only('tablet')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.05rem',
      height: '3rem',
      marginTop: '-0.95rem',
      marginBottom: '0.5rem',
    },
  }),
  detail: {
    display: 'flex',
    fontStyle: 'normal',
  },
  detailLabel: {
    fontWeight: 'bold',
    height: '1.875rem',
    whiteSpace: 'nowrap',
    paddingRight: '0.25rem',
  },
}

const AutopayDetailsSection = ({
  setOpenAutopayCancelDialog,
  handlePayOnAccountAutoPay,
  client,
}: AutopayDetailsSectionProps) => {
  const { t } = useTranslation()
  const { isMobile, isTablet, isDesktop } = useDeviceType()

  const [expanded, setExpanded] = useState<boolean>(false)
  const [nextPaymentDate, setNextPaymentDate] = useState<Date>()
  const [frequency, setFrequency] = useState<FrequencyOption | undefined>(
    undefined,
  )
  const [paymentLimit, setPaymentLimit] = useState<number | undefined>(
    undefined,
  )
  const [paymentMethodType, setPaymentMethodType] = useState<string>('')
  const [editAutoPayDetails, setEditAutoPayDetails] = useState<
    AutopayDetailsResponseViewModel | undefined
  >(undefined)

  const cemFeatures = useSelector(getCemFeatures)
  const userInfo = useSelector(getUserInfo)
  const iconSize = isDesktop ? 31 : 24

  const invoicingAutopayCancelEnabled = isFeatureFlagEnabled(
    'Invoicing_AutopayCancel',
    userInfo.emailAddress,
    cemFeatures,
  )

  const getDetailsSection = () => (
    <>
      <Box sx={styles.detail}>
        <Box sx={styles.detailLabel}>
          {t('Invoicing.AutopayDetailsSection.NextPaymentDate')}:
        </Box>
        <Box>
          {nextPaymentDate?.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Box>
      </Box>
      <Box sx={styles.detail}>
        <Box sx={styles.detailLabel}>
          {t('Invoicing.AutopayDetailsSection.Frequency')}:
        </Box>
        <Box>{t(frequency?.label || '').toLowerCase()}</Box>
      </Box>
      <Box sx={styles.detail}>
        <Box sx={styles.detailLabel}>
          {t('Invoicing.AutopayDetailsSection.Limit')}:
        </Box>
        <Box>
          {paymentLimit && paymentLimit > 0
            ? formatCurrency(paymentLimit)
            : t('Invoicing.AutopayDetailsSection.None').toLowerCase()}
        </Box>
      </Box>
      <Box sx={styles.detail}>
        <Box sx={styles.detailLabel}>
          {t('Invoicing.AutopayDetailsSection.PaymentMethod')}:
        </Box>
        <Box>{paymentMethodType.toLowerCase()}</Box>
      </Box>
    </>
  )

  const getDefaultHeaderButtonSection = () => (
    <>
      <Typography sx={styles.autoPay} data-testid="AutopayOnText">
        {t('Invoicing.AutopayOn')}
      </Typography>
      <Button
        data-testid={`Btn_AutopayDetailsSection_${
          invoicingAutopayCancelEnabled
            ? t('Invoicing.AccountInformationCard.Cancel')
            : t('Invoicing.Update')
        }Autopay`}
        aria-label="Update autopay"
        sx={
          invoicingAutopayCancelEnabled
            ? styles.cancelButton
            : styles.updateButton
        }
        disabled={!client}
        disableFocusRipple
        disableRipple
        onClick={(e) =>
          invoicingAutopayCancelEnabled
            ? setOpenAutopayCancelDialog(true)
            : handlePayOnAccountAutoPay(e, false)
        }>
        {invoicingAutopayCancelEnabled
          ? t('Invoicing.AccountInformationCard.Cancel').toLowerCase()
          : t('Invoicing.Update')}
      </Button>
    </>
  )

  useEffect(() => {
    const clientId = Number(client?.clientId)
    if (clientId) {
      api.finance.payment_GetAutopayDetails(clientId).then((response) => {
        if (response?.data) {
          setEditAutoPayDetails(response?.data)
        }
      })
    }
  }, [])

  useEffect(() => {
    const frequencyVal = AutoPayFrequencyIntervals.find(
      (item) => item.value === editAutoPayDetails?.recurrenceInterval,
    )
    const paymentMethodValue =
      editAutoPayDetails?.paymentMethod === 1
        ? t('Invoicing.AutopayDetailsSection.BankAccount')
        : t('Invoicing.AutopayDetailsSection.CreditCard')
    const nextPaymentDateVal =
      getNextProcessingDate(
        editAutoPayDetails?.startDate,
        editAutoPayDetails?.recurrenceInterval,
      ) ?? ''
    setNextPaymentDate(nextPaymentDateVal)
    setFrequency(frequencyVal)
    setPaymentLimit(editAutoPayDetails?.paymentLimit)
    setPaymentMethodType(paymentMethodValue)
  }, [editAutoPayDetails])

  return (
    <>
      <Box sx={styles.autoPayIconContainer}>
        <Box id="test" sx={styles.checkCircle}>
          <AutopayIcon size={isMobile ? 18 : iconSize} />
        </Box>
        {isDesktop && (
          <Box
            sx={{
              padding: '0rem 0rem 0rem 0.5rem',
              width: '100%',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                borderBottom: invoicingAutopayCancelEnabled
                  ? '0.0938rem solid rgba(0, 0, 0, 0.12)'
                  : 'unset', // using rgbs to match the default MUI theme
              }}>
              <Typography sx={styles.autoPay} data-testid="AutopayOnText">
                {t('Invoicing.AutopayOn')}
              </Typography>
              <Button
                data-testid={`Btn_Autopay_${
                  invoicingAutopayCancelEnabled
                    ? t('Invoicing.AutopayDetailsSection.Cancel')
                    : t('Invoicing.Update')
                }Autopay`}
                aria-label={`${
                  invoicingAutopayCancelEnabled
                    ? t('Invoicing.AutopayDetailsSection.Cancel')
                    : t('Invoicing.Update')
                } autopay`}
                sx={
                  invoicingAutopayCancelEnabled
                    ? styles.cancelButton
                    : styles.updateButton
                }
                disabled={!client}
                disableFocusRipple
                disableRipple
                onClick={(e) => {
                  e.stopPropagation()
                  if (invoicingAutopayCancelEnabled) {
                    setOpenAutopayCancelDialog(true)
                  } else {
                    handlePayOnAccountAutoPay(e, false)
                  }
                }}>
                {invoicingAutopayCancelEnabled
                  ? t('Invoicing.AutopayDetailsSection.Cancel').toLowerCase()
                  : t('Invoicing.Update')}
              </Button>
              {invoicingAutopayCancelEnabled && (
                <Button
                  id={`Autopay_${t(
                    'Invoicing.AutopayDetailsSection.Details',
                  )}_Button`}
                  aria-controls={`Autopay_${t(
                    'Invoicing.AutopayDetailsSection.Details',
                  )}_Section`}
                  data-testid={`Btn_Autopay_${t(
                    'Invoicing.AutopayDetailsSection.Details',
                  )}`}
                  aria-label={`autopay ${t(
                    'Invoicing.AutopayDetailsSection.Details',
                  )}`}
                  aria-expanded={expanded}
                  sx={{ fontSize: '0.75rem', fontFamily: 'Prelo-Book' }}
                  disabled={!client}
                  disableFocusRipple
                  disableRipple
                  onClick={() => {
                    setExpanded(!expanded)
                  }}
                  endIcon={
                    <ExpandMore
                      sx={{
                        transition: 'transform 0.3s',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  }>
                  {t('Invoicing.AutopayDetailsSection.Details')}
                </Button>
              )}
            </Box>
            {invoicingAutopayCancelEnabled && (
              <Box
                id={`Autopay_${t(
                  'Invoicing.AutopayDetailsSection.Details',
                )}_Section`}
                aria-labelledby={`Autopay_${t(
                  'Invoicing.AutopayDetailsSection.Details',
                )}_Button`}
                sx={{
                  backgroundColor: tokens.colors.white,
                  paddingLeft: '0rem',
                  paddingRight: '0rem',
                  paddingBottom: '0rem',
                  width: '100%',
                  position: 'relative',
                  transitionDuration: '252ms',
                  transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  overflow: expanded ? 'visible' : 'hidden',
                  visibility: expanded ? 'visible' : 'hidden',
                }}
                data-testid="Lbl_AutopayDetailsSection_Autopay_CancelDetails"
                aria-hidden={!expanded}
                role="region">
                <Collapse in={expanded}> {getDetailsSection()}</Collapse>
              </Box>
            )}
          </Box>
        )}
        {isTablet && getDefaultHeaderButtonSection()}
        {isMobile && <Box>{getDefaultHeaderButtonSection()}</Box>}
      </Box>
      {isTablet && getDetailsSection()}
    </>
  )
}

export default AutopayDetailsSection
